/**
 * Thin wrapper around the search-insights library (loaded as a deferred CDN
 * script in snippets/algolia-config.liquid). Exposes window.MaiAlgoliaInsights
 * so algolia-search.js and analytics-helpers.js can fire click / view /
 * add-to-cart events without each knowing the Insights API directly.
 *
 * Identity:
 *   - Authenticated users → Firebase UID via setAuthenticatedUserToken (matches
 *     the Segment .identify() call in analytics-helpers.js).
 *   - Anonymous users → Segment's anonymousId via setUserToken (single visitor
 *     ID across both analytics systems, no extra cookie surface).
 *
 * Search snapshot:
 *   - Each search response (queryID + hits) is merged into a per-objectID map
 *     in sessionStorage. Click / add-to-cart events look up queryID + position
 *     by the clicked objectID, so attribution survives over-fetch (multiple
 *     queryIDs per visible page), pagination (back/forward across pages), and
 *     the navigation to the PDP.
 *
 * objectID = Firestore item ID = Shopify variant SKU. See plan / syncItems.ts.
 */
(function () {
  'use strict';

  if (!window.aa || !window.ALGOLIA_CONFIG || !window.ALGOLIA_CONFIG.appId) return;

  var INDEX = window.ALGOLIA_CONFIG.indexName;
  var SNAPSHOT_KEY = 'mai_algolia_search';
  var SNAPSHOT_TTL_MS = 30 * 60 * 1000; // mirrors mai_source TTL

  // Algolia caps individual view/click events at 20 objectIDs. Chunk to stay
  // under that for full-page view events.
  var MAX_OBJECT_IDS_PER_EVENT = 20;

  // Soft cap on accumulated snapshot size to keep sessionStorage usage bounded.
  // Once exceeded we drop the oldest entries (FIFO by insertion order).
  var MAX_SNAPSHOT_ENTRIES = 1000;

  // ── Identity ─────────────────────────────────────────────────

  var _identityPromise = null;

  // Async-resolve identity. The synchronous fast path below covers most cases;
  // this handles two stragglers:
  //   - Segment hadn't written `ajs_anonymous_id` to localStorage yet at IIFE
  //     time (e.g. brand-new visitor whose first pageview is /search) — once
  //     Segment loads, `analytics.user().anonymousId()` returns it.
  //   - Firebase UID wasn't cached in sessionStorage yet (cold session) —
  //     `waitForAuthState` returns it after a few hundred ms.
  function resolveIdentity() {
    if (_identityPromise) return _identityPromise;
    _identityPromise = new Promise(function (resolve) {
      var anon = null;
      try {
        if (window.analytics && window.analytics.user) {
          anon = window.analytics.user().anonymousId() || null;
        }
      } catch { /* ignore */ }

      var cachedUid = sessionStorage.getItem('firebaseUserId');
      if (cachedUid) return resolve({ uid: cachedUid, anon: anon });
      if (window.firebaseAuth && window.firebaseAuth.waitForAuthState) {
        window.firebaseAuth.waitForAuthState(2500)
          .then(function (user) { resolve({ uid: user && user.uid ? user.uid : null, anon: anon }); })
          .catch(function () { resolve({ uid: null, anon: anon }); });
      } else {
        resolve({ uid: null, anon: anon });
      }
    });
    return _identityPromise;
  }

  // ── Init ─────────────────────────────────────────────────────

  // The aa queue (set up in algolia-config.liquid) is FIFO — search-insights
  // drains it in insertion order after the library loads. So init → setUserToken
  // → setAuthenticatedUserToken → first viewedObjectIDs (queued later by
  // recordSearch) all run in the right order even when several are queued
  // before the library replaces the stub. Don't reorder these.
  window.aa('init', {
    appId: window.ALGOLIA_CONFIG.appId,
    apiKey: window.ALGOLIA_CONFIG.apiKey,
    // We provide our own user token (Segment anonymousId / Firebase UID);
    // disable the library's own cookie so we don't introduce a new visitor ID.
    useCookie: false,
    // Allow events queued before authenticatedUserToken resolves to send.
    partial: true,
  });

  // Set userToken synchronously from Segment's anonymousId so it's queued
  // BEFORE the first viewedObjectIDs fired by recordSearch. Read directly
  // from localStorage['ajs_anonymous_id'] (where Segment stores it as a
  // JSON-encoded string) — calling window.analytics.user().anonymousId() may
  // return undefined here because Segment's library is still loading async
  // when this deferred script runs.
  try {
    var raw = localStorage.getItem('ajs_anonymous_id');
    if (raw) {
      // Stored as JSON: '"<uuid>"'. Strip the surrounding quotes.
      var syncAnon = raw.charAt(0) === '"' ? JSON.parse(raw) : raw;
      if (syncAnon) window.aa('setUserToken', syncAnon);
    }
  } catch { /* ignore */ }

  // Cached firebaseUserId (set by firebase-auth.js into sessionStorage on
  // every auth-state change) is also synchronous — set authenticatedUserToken
  // immediately when present to maximize coverage of the first event burst.
  try {
    var syncUid = sessionStorage.getItem('firebaseUserId');
    if (syncUid) window.aa('setAuthenticatedUserToken', syncUid);
  } catch { /* ignore */ }

  // Late fallbacks for the two stragglers (see resolveIdentity comment).
  // Idempotent overwrite — the library accepts repeated set* calls.
  resolveIdentity().then(function (id) {
    if (id.anon) window.aa('setUserToken', id.anon);
    if (id.uid) window.aa('setAuthenticatedUserToken', id.uid);
  });

  // ── Search snapshot (sessionStorage) ─────────────────────────
  //
  // Shape: { ts, byObjectID: { [objectID]: { queryID, position, addedAt } } }
  //
  // Each search response merges into byObjectID (last write wins per ID — if
  // a hit reappears in a later search, the most recent queryID is the one
  // attributable to a click). `position` is the absolute Algolia position
  // (offset + index + 1). `addedAt` lets us evict oldest entries when the
  // map exceeds MAX_SNAPSHOT_ENTRIES.

  function readSnapshot() {
    try {
      var raw = sessionStorage.getItem(SNAPSHOT_KEY);
      if (!raw) return { ts: 0, byObjectID: {} };
      var data = JSON.parse(raw);
      if (Date.now() - data.ts > SNAPSHOT_TTL_MS) return { ts: 0, byObjectID: {} };
      if (!data.byObjectID) data.byObjectID = {};
      return data;
    } catch {
      return { ts: 0, byObjectID: {} };
    }
  }

  function writeSnapshot(snap) {
    snap.ts = Date.now();
    try {
      sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snap));
    } catch { /* sessionStorage full / unavailable */ }
  }

  function evictIfTooLarge(snap) {
    var keys = Object.keys(snap.byObjectID);
    if (keys.length <= MAX_SNAPSHOT_ENTRIES) return;
    keys.sort(function (a, b) { return snap.byObjectID[a].addedAt - snap.byObjectID[b].addedAt; });
    var dropCount = keys.length - MAX_SNAPSHOT_ENTRIES;
    for (var i = 0; i < dropCount; i++) delete snap.byObjectID[keys[i]];
  }

  // Returns the queryID + 1-indexed Algolia position for the given objectID,
  // or null if it's not in the snapshot (e.g. the user clicked a card from a
  // collection page, not a search). NB: Algolia positions are 1-indexed; the
  // click handler in analytics-helpers.js uses a separate 0-indexed DOM
  // position for Segment's `Product List Viewed` — don't conflate them.
  function lookupPosition(objectID) {
    var snap = readSnapshot();
    var entry = snap.byObjectID[objectID];
    if (!entry) return null;
    return { queryID: entry.queryID, position: entry.position };
  }

  // ── Public API ───────────────────────────────────────────────

  /**
   * Called by algolia-search.js after every search response. Merges each
   * hit's queryID + position into the snapshot map and fires
   * viewedObjectIDsAfterSearch for impression analytics.
   *
   * @param queryID String from data.queryID
   * @param hits Array of hits (each must have an objectID)
   * @param baseOffset Absolute Algolia offset of this batch (= search request
   *   offset). Position of hit i becomes baseOffset + i + 1.
   */
  function recordSearch(queryID, hits, baseOffset) {
    if (!queryID || !hits || !hits.length) return;
    var objectIDs = [];
    var snap = readSnapshot();
    var now = Date.now();
    var offset = typeof baseOffset === 'number' ? baseOffset : 0;
    for (var i = 0; i < hits.length; i++) {
      var h = hits[i];
      if (!h || !h.objectID) continue;
      objectIDs.push(h.objectID);
      snap.byObjectID[h.objectID] = {
        queryID: queryID,
        position: offset + i + 1,
        addedAt: now,
      };
    }
    if (!objectIDs.length) return;
    evictIfTooLarge(snap);
    writeSnapshot(snap);
    // Chunk to respect Algolia's 20-objectID-per-event cap. Note: search-insights
    // v2.x exposes only `viewedObjectIDs` (no `viewedObjectIDsAfterSearch` —
    // calling it logs a "method doesn't exist" warning). The Algolia dashboard's
    // CTR-by-query metric is computed automatically from search → click events
    // sharing a queryID, not from view events; view events feed personalization
    // signals ("user saw item X") that don't need queryID attribution.
    for (var j = 0; j < objectIDs.length; j += MAX_OBJECT_IDS_PER_EVENT) {
      window.aa('viewedObjectIDs', {
        index: INDEX,
        eventName: 'Products Viewed',
        objectIDs: objectIDs.slice(j, j + MAX_OBJECT_IDS_PER_EVENT),
      });
    }
  }

  /**
   * Called by analytics-helpers.js when a card on the search surface is
   * clicked. Returns the queryID so the caller can stamp it into mai_source
   * for the downstream add-to-cart event.
   */
  function recordClick(objectID) {
    if (!objectID) return null;
    var pos = lookupPosition(objectID);
    if (!pos) return null;
    window.aa('clickedObjectIDsAfterSearch', {
      index: INDEX,
      eventName: 'Product Clicked',
      queryID: pos.queryID,
      objectIDs: [objectID],
      positions: [pos.position],
    });
    return { queryID: pos.queryID };
  }

  /**
   * Called by analytics-helpers.js after a successful cart/add.js when the
   * consumed source has a queryID (i.e. the user came from search).
   */
  function recordAddToCart(objectID, queryID, price) {
    if (!objectID || !queryID) return;
    var objectData = [{ quantity: 1, queryID: queryID }];
    if (typeof price === 'number' && isFinite(price)) objectData[0].price = price;
    window.aa('addedToCartObjectIDsAfterSearch', {
      index: INDEX,
      eventName: 'Product Added to Cart',
      // eventSubtype lets the Algolia dashboard filter add-to-cart vs. other
      // conversion events distinctly (a future purchase event would use
      // eventSubtype: 'purchase' on convertedObjectIDsAfterSearch).
      eventSubtype: 'addToCart',
      queryID: queryID,
      objectIDs: [objectID],
      objectData: objectData,
      currency: (window.ALGOLIA_CONFIG && window.ALGOLIA_CONFIG.currency) || 'SEK',
    });
  }

  window.MaiAlgoliaInsights = {
    recordSearch: recordSearch,
    recordClick: recordClick,
    recordAddToCart: recordAddToCart,
  };
})();
